import React, {useState} from "react"
import {
  ResponsiveContainer,
  ComposedChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ZAxis,
  Scatter,
  Cell,
  Line,
  Tooltip as ChartTooltip
} from "recharts"

import FormattedMessage, {injectIntl, useIntl} from "@irati/utils/lang"
import {OrganisationalSummaryCategory, PaymentCurrencyType} from "@irati/types"
import theme from "@irati/ui/src/global/theme"
import {formatCurrency} from "@irati/utils/currency/formatCurrency"
import Flex from "@irati/ui/src/Flex"
import {Label, H4, Body} from "@irati/ui/src/Typography"

import BenchmarkText from "../BenchmarkGraph/BenchmarkText"
import {ChartWrapper, TooltipWrapper} from "./styled"
import {TrendTypes} from "./enums"

interface Row {
  value: number
  date: string
}

interface Props {
  selectedTrendRate: TrendTypes
  selectedCategory: OrganisationalSummaryCategory
  selectedTrendRateLabel: string
  currencyCode: PaymentCurrencyType
  data: Row[]
  selectedBenchmark?: string
  companySize?: string
}

const BarChart: React.FC<Props> = (props) => {
  const [focusBarIndex, setFocusBarIndex] = useState<number | null>(null)
  const [mouseLeave, setMouseLeave] = useState(true)
  const {formatNumber} = useIntl()

  const tickFormatter = (item: number) => {
    const {selectedTrendRate, currencyCode} = props
    if (selectedTrendRate === TrendTypes.engagementRate || selectedTrendRate === TrendTypes.savingsPercentage) {
      return `${Math.round(item)}%`
    }
    if (selectedTrendRate === TrendTypes.savings) {
      return formatCurrency({value: item, currencyCode, isCents: false})
    }
    return formatNumber(item)
  }
  const onHoverState = (state: any) => {
    if (state.isTooltipActive) {
      setFocusBarIndex(state.activeTooltipIndex)
      setMouseLeave(false)
    } else {
      setFocusBarIndex(null)
      setMouseLeave(true)
    }
  }

  const getColor = () => {
    const {selectedCategory, selectedTrendRate} = props

    if (selectedTrendRate === TrendTypes.engagementRate || selectedTrendRate === TrendTypes.usersEngaged) {
      return theme.colors.primary1
    }
    if (selectedCategory === OrganisationalSummaryCategory.Perks) {
      if (selectedTrendRate === TrendTypes.burned) {
        return theme.colors.tertiary.f_400
      }
      return theme.colors.primary1
    }
    return theme.colors.primary2
  }

  const handleMouseLeave = () => {
    setFocusBarIndex(null)
    setMouseLeave(true)
  }

  const {data, selectedTrendRateLabel, selectedTrendRate, selectedBenchmark, companySize} = props

  const isStackedSavingsBarChart = selectedTrendRate === TrendTypes.savings
  const isMultiRewardPointsBarChart = selectedTrendRate === TrendTypes.rewardPoints
  const isNormalBarChart = selectedTrendRate !== TrendTypes.savings && selectedTrendRate !== TrendTypes.rewardPoints

  const digitalVouchers = data[focusBarIndex ? focusBarIndex : 0]?.value?.digitalVouchers
  const flexiPoints = data[focusBarIndex ? focusBarIndex : 0]?.value?.flexiPoints
  const rewardPoints = data[focusBarIndex ? focusBarIndex : 0]?.value?.rewardPoints
  const discountedVouchers = data[focusBarIndex ? focusBarIndex : 0]?.value?.discountedVouchers

  return (
    <ChartWrapper index={focusBarIndex + 1} onMouseLeave={handleMouseLeave}>
      <ResponsiveContainer height={500}>
        <ComposedChart
          width={500}
          height={400}
          data={data}
          margin={{
            top: 0,
            right: 0,
            bottom: 0,
            left: 8
          }}
          onMouseMove={(state: any) => onHoverState(state)}>
          <CartesianGrid vertical={false} />
          <XAxis dataKey="date" axisLine={false} tickLine={false} />
          <YAxis tickCount={6} tickFormatter={tickFormatter} axisLine={false} tickLine={false} />
          <ZAxis dataKey="value" range={[30, 30]} />
          <ChartTooltip
            content={
              <TooltipWrapper padding={2} margin={2}>
                <Label color={theme.colors.primary1}>
                  <FormattedMessage id={selectedTrendRateLabel} />
                </Label>
                <Flex marginTop={1} marginBottom={1} justifyContent="space-between" gap="16px">
                  <Label>{data[focusBarIndex ? focusBarIndex : 0]?.date}</Label>
                  {isMultiRewardPointsBarChart && (
                    <Flex>
                      <H4>
                        <FormattedMessage id="admin.reporting.trendReports.download.given" />{" "}
                        {tickFormatter(data[focusBarIndex ? focusBarIndex : 0]?.value?.rewardPointsGiven)},
                      </H4>
                      <H4 marginLeft>
                        <FormattedMessage id="admin.reporting.trendReports.download.purchased" />{" "}
                        {tickFormatter(data[focusBarIndex ? focusBarIndex : 0]?.value?.rewardPointsPurchased)}
                      </H4>
                    </Flex>
                  )}
                  {isNormalBarChart && selectedTrendRate !== TrendTypes.engagementRate && (
                    <H4>{tickFormatter(data[focusBarIndex ? focusBarIndex : 0]?.value)}</H4>
                  )}
                </Flex>
                {isNormalBarChart && selectedTrendRate === TrendTypes.engagementRate && (
                  <>
                    <Flex alignItems="center" justifyContent="space-between" gap={4}>
                      <Flex direction="column" width="100%">
                        <Body small maxWidth="200px">
                          {selectedBenchmark}
                        </Body>
                        <Body small>
                          <FormattedMessage id="admin.reporting.trendReports.barChartKey.employees" values={{companySize}} />
                        </Body>
                      </Flex>
                      <H4>{tickFormatter(data[focusBarIndex ? focusBarIndex : 0]?.benchmark)}</H4>
                    </Flex>
                    <Flex alignItems="center" justifyContent="space-between" marginTop={1} gap={4}>
                      <Body small>
                        <FormattedMessage id="admin.reporting.trendReports.barChartKey.yourCompany" />
                      </Body>
                      <H4>{tickFormatter(data[focusBarIndex ? focusBarIndex : 0]?.value)}</H4>
                    </Flex>
                  </>
                )}
                {isStackedSavingsBarChart && (
                  <>
                    <H4 marginBottom>
                      <FormattedMessage
                        id="admin.reporting.trendReports.download.total"
                        values={{amount: tickFormatter(digitalVouchers + flexiPoints + rewardPoints + discountedVouchers)}}
                      />
                    </H4>
                    {discountedVouchers > 0 && (
                      <Body small>
                        <FormattedMessage
                          id="admin.reporting.trendReports.barChartKey.discountedVouchers"
                          values={{amount: tickFormatter(discountedVouchers)}}
                        />
                      </Body>
                    )}
                    {rewardPoints > 0 && (
                      <Body small>
                        <FormattedMessage
                          id="admin.reporting.trendReports.barChartKey.reward"
                          values={{amount: tickFormatter(rewardPoints)}}
                        />
                      </Body>
                    )}
                    {flexiPoints > 0 && (
                      <Body small>
                        <FormattedMessage
                          id="admin.reporting.trendReports.barChartKey.flexi "
                          values={{amount: tickFormatter(flexiPoints)}}
                        />
                      </Body>
                    )}
                    {digitalVouchers > 0 && (
                      <Body small>
                        <FormattedMessage
                          id="admin.reporting.trendReports.barChartKey.digitalVouchers"
                          values={{amount: tickFormatter(digitalVouchers)}}
                        />
                      </Body>
                    )}
                  </>
                )}
              </TooltipWrapper>
            }
          />
          {isMultiRewardPointsBarChart && (
            <>
              <Bar dataKey="value.rewardPointsGiven" fill={theme.colors.utility.gold} barSize={40}>
                {data.map((_, index) => {
                  return <Cell key={index} opacity={index === data.length - 1 ? 0.5 : 1} />
                })}
              </Bar>
              <Bar dataKey="value.rewardPointsPurchased" fill={theme.colors.primary2} barSize={40}>
                {data.map((_, index) => {
                  return <Cell key={index} opacity={index === data.length - 1 ? 0.5 : 1} />
                })}
              </Bar>
            </>
          )}
          {isStackedSavingsBarChart && (
            <>
              <Bar dataKey="value.digitalVouchers" stackId="a" fill={theme.colors.primary1} barSize={40}>
                {data.map((_, index) => {
                  return <Cell key={index} opacity={index === data.length - 1 ? 0.5 : 1} />
                })}
              </Bar>
              <Bar dataKey="value.flexiPoints" stackId="a" fill={theme.colors.tertiary.f_400} barSize={40}>
                {data.map((_, index) => {
                  return <Cell key={index} opacity={index === data.length - 1 ? 0.5 : 1} />
                })}
              </Bar>
              <Bar dataKey="value.rewardPoints" stackId="a" fill={theme.colors.utility.gold} barSize={40}>
                {data.map((_, index) => {
                  return <Cell key={index} opacity={index === data.length - 1 ? 0.5 : 1} />
                })}
              </Bar>
              <Bar dataKey="value.discountedVouchers" stackId="a" fill={theme.colors.primary2} barSize={40} opacity={0.5}>
                {data.map((_, index) => {
                  return <Cell key={index} opacity={index === data.length - 1 ? 0.5 : 1} />
                })}
              </Bar>
            </>
          )}
          {isNormalBarChart && (
            <Bar id="adminSection_trendRatesBarChart" dataKey="value" fill={getColor()} barSize={40}>
              {data.map((_, index) => {
                return <Cell key={index} opacity={index === data.length - 1 ? 0.5 : 1} />
              })}
            </Bar>
          )}
          {selectedBenchmark && (
            <Line
              type="monotone"
              dataKey="benchmark"
              stroke={theme.colors.primary1}
              strokeDasharray="4 4"
              dot={<BenchmarkText text={selectedBenchmark} finalIndex={data.length - 1} />}
            />
          )}
          {!mouseLeave && <Scatter dataKey="value" shape="circle" stroke={theme.colors.primary2} fill={theme.colors.primary1} />}
        </ComposedChart>
      </ResponsiveContainer>
    </ChartWrapper>
  )
}

export default injectIntl(BarChart)
